import axios from "axios";
import {
  CreateGameRequest,
  CreateGameResponse,
  MakeMoveRequest,
  MakeMoveResponse,
  Game,
  CashOutSignatureResponse,
  MarkCashedOutResponse,
  SourceData,
  GameConfig,
} from "../types";
import { authService } from "./auth";

// Get API base URL from environment or default
export const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'development' ? 'http://localhost:3001/api/v1' : '/api/v1');

// Enhanced error class to preserve backend error details
export class ApiError extends Error {
  statusCode: number;
  code: string;
  timestamp: string;

  constructor(
    message: string,
    statusCode: number = 500,
    code: string = "API_ERROR",
    timestamp?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.code = code;
    this.timestamp = timestamp || new Date().toISOString();
  }
}

// Helper function to extract error details from axios error
function handleApiError(error: any): never {
  if (error.response?.data) {
    // Backend returned structured error response
    const { error: message, code, timestamp } = error.response.data;
    const statusCode = error.response.status;

    throw new ApiError(
      message || "An error occurred",
      statusCode,
      code || "API_ERROR",
      timestamp,
    );
  } else if (error.request) {
    // Network error or no response
    throw new ApiError(
      "Network error - please check your connection",
      0,
      "NETWORK_ERROR",
    );
  } else {
    // Other error
    throw new ApiError(
      error.message || "Unknown error occurred",
      500,
      "UNKNOWN_ERROR",
    );
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      authService.logout();
      window.location.reload(); // Refresh to show login
    }
    return Promise.reject(error);
  },
);

export class ApiService {
  static async createGame(
    request: CreateGameRequest,
  ): Promise<CreateGameResponse> {
    try {
      const response = await api.post("/game/create", request);
      return response.data;
    } catch (error) {
      console.error("Error creating game:", error);
      handleApiError(error);
    }
  }

  static async makeMove(
    gameId: string,
    request: MakeMoveRequest,
  ): Promise<MakeMoveResponse> {
    try {
      const response = await api.post(`/game/${gameId}/move`, request);
      return response.data;
    } catch (error) {
      console.error("Error making move:", error);
      handleApiError(error);
    }
  }

  static async skipRow(gameId: string): Promise<MakeMoveResponse> {
    try {
      const response = await api.post(`/game/${gameId}/move`, { 
        skipRow: true 
      });
      return response.data;
    } catch (error) {
      console.error("Error skipping row:", error);
      handleApiError(error);
    }
  }

  static async markCashedOut(gameId: string): Promise<MarkCashedOutResponse> {
    try {
      const response = await api.post(`/game/${gameId}/mark-cashed-out`, {});
      return response.data;
    } catch (error) {
      console.error("Error mark cashed out:", error);
      handleApiError(error);
    }
  }

  static async getGame(gameId: string): Promise<Game> {
    try {
      const response = await api.get(`/game/${gameId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting game:", error);
      handleApiError(error);
    }
  }

  static async getCashOutSignature(
    gameId: string,
    createGameAgain?: boolean,
  ): Promise<CashOutSignatureResponse & { newGame?: CreateGameResponse }> {
    try {
      const response = await api.post(`/game/${gameId}/cashout-signature`, {
        createGameAgain,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting cash out signature:", error);
      handleApiError(error);
    }
  }

  static async getActiveGame(): Promise<{
    activeGame: Game | null;
    hasActiveGame: boolean;
  }> {
    try {
      const response = await api.get("/game/active");
      return response.data;
    } catch (error) {
      console.error("Error getting active game:", error);
      handleApiError(error);
    }
  }

  static async verifyGame(
    gameId: string,
    seed: string,
    rowConfigs: any[],
    gameSeedHash: string,
  ) {
    try {
      const response = await api.post(`/game/${gameId}/verify`, {
        seed,
        rowConfigs,
        gameSeedHash,
        version: "v1",
      });
      return response.data;
    } catch (error) {
      console.error("Error verifying game:", error);
      handleApiError(error);
    }
  }

  static async sendSourceData(
    sourceData: SourceData,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post("/analytics/source", sourceData);
      return response.data;
    } catch (error) {
      console.error("Error sending source data:", error);
      handleApiError(error);
    }
  }

  static async getSourceData(): Promise<{
    sourceData: SourceData | null;
    hasSourceData: boolean;
  }> {
    try {
      const response = await api.get("/analytics/source");
      return response.data.data;
    } catch (error) {
      console.error("Error getting source data:", error);
      handleApiError(error);
    }
  }

  static async getGameConfig(): Promise<GameConfig> {
    try {
      const response = await api.get("/config");
      return response.data;
    } catch (error) {
      console.error("Error getting utility config:", error);
      handleApiError(error);
    }
  }

  static async getAvailableLeaderboardWeeks(): Promise<{
    weeks: Array<{
      weekStartDate: string;
      weekEndDate: string;
      isActive: boolean;
      completedAt?: string;
    }>;
  }> {
    try {
      const response = await api.get("/leaderboard/weeks");
      return response.data;
    } catch (error) {
      console.error("Error getting available leaderboard weeks:", error);
      handleApiError(error);
    }
  }

  static async getHistoricLeaderboard(weekStartDate: string): Promise<any> {
    try {
      const response = await api.get(`/leaderboard/history/${weekStartDate}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting historic leaderboard for ${weekStartDate}:`, error);
      handleApiError(error);
    }
  }

  static async recoverStreak(txHash: string): Promise<{
    success: boolean;
    message: string;
    recoveredStreak: number;
  }> {
    try {
      const response = await api.post('/leaderboard/recover-streak', {
        txHash,
      });
      return response.data;
    } catch (error) {
      console.error('Error recovering streak:', error);
      handleApiError(error);
    }
  }
}
